import { apiFetch, apiFetchWithRetry } from '@/lib/apiFetch';
import { handleUnauthorized } from './UserManagmentServices';

// --- Types ---

export interface PublicReviewItem {
  id: string;
  authorDisplayName: string;
  rating: number;
  comment: string;
  createdAt: string;
  updatedAt: string;
  teacherReply: string | null;
  teacherReplyAt: string | null;
}

export interface PublicTeacherReviewsResponse {
  /** NULL when ratingCount is 0 — never coalesced to 0. */
  ratingAvg: number | null;
  ratingCount: number;
  content: PublicReviewItem[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface MyReview {
  id: string;
  rating: number;
  comment: string;
  createdAt: string;
  updatedAt: string;
  teacherReply: string | null;
  teacherReplyAt: string | null;
  /** Moderation state of the caller's own review (cluster 2/4): PENDING | PUBLISHED | HELD | REMOVED.
   *  Optional so pre-cluster-6 callers/fixtures that omit it still type-check. */
  status?: string;
  /** Populated when a review was held/removed by moderation; null otherwise. */
  moderationReason?: string | null;
}

export interface ReviewEligibilityResponse {
  eligible: boolean;
  review: MyReview | null;
}

export interface TeacherOwnReviewResponse {
  id: string;
  authorDisplayName: string;
  rating: number;
  comment: string;
  createdAt: string;
  updatedAt: string;
  teacherReply: string | null;
  teacherReplyAt: string | null;
  hidden: boolean;
  /** Moderation state of the teacher's reply (cluster 5): PENDING | PUBLISHED | HELD | REMOVED | null (no reply). */
  replyStatus: string | null;
  /** Set when the teacher has flagged this review for moderator review (cluster 4); null otherwise. */
  flaggedAt: string | null;
  /** The reason the teacher supplied when flagging, if any. */
  flagReason: string | null;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface UpsertReviewRequest {
  rating: number;
  comment: string;
}

// --- Public review API (no auth) ---

export async function getPublicTeacherReviews(
  profileIdOrHandle: string,
  page = 0,
  size = 10
): Promise<PublicTeacherReviewsResponse> {
  const response = await apiFetchWithRetry(
    `/api/v1/marketplace/teachers/${profileIdOrHandle}/reviews?page=${page}&size=${size}`,
    { headers: { 'Content-Type': 'application/json' } }
  );
  if (!response.ok) throw new Error(`Failed to fetch teacher reviews: ${response.status}`);
  return response.json();
}

// --- Student review API ---

export async function getMyReviewFor(
  accessToken: string,
  teacherUserId: string
): Promise<ReviewEligibilityResponse> {
  const response = await apiFetchWithRetry(`/api/v1/reviews/me/teachers/${teacherUserId}`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch review eligibility: ${response.status}`);
  return response.json();
}

export async function upsertReview(
  accessToken: string,
  teacherUserId: string,
  body: UpsertReviewRequest
): Promise<MyReview> {
  const response = await apiFetch(`/api/v1/reviews/teachers/${teacherUserId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });
  if (response.status === 401) handleUnauthorized();
  // 201 (created) or 200 (edited) are both success.
  if (!response.ok) throw new Error(`Failed to submit review: ${response.status}`);
  return response.json();
}

// --- Teacher review API ---

export async function getMyTeacherReviews(
  accessToken: string,
  page = 0,
  size = 20
): Promise<PageResponse<TeacherOwnReviewResponse>> {
  const response = await apiFetchWithRetry(
    `/api/v1/reviews/teacher/me?page=${page}&size=${size}`,
    { headers: { 'Authorization': `Bearer ${accessToken}` } }
  );
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to fetch teacher reviews: ${response.status}`);
  return response.json();
}

export async function upsertReply(
  accessToken: string,
  reviewId: string,
  reply: string
): Promise<TeacherOwnReviewResponse> {
  const response = await apiFetch(`/api/v1/reviews/${reviewId}/reply`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ reply }),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to submit reply: ${response.status}`);
  return response.json();
}

/**
 * Flag a review for moderator review (teacher action, cluster 4). Does NOT hide the
 * review — it merely queues it for a human moderator. `reason` is optional.
 * Mutation (never retried). Returns the updated review with its new `flaggedAt`.
 */
export async function flagReview(
  accessToken: string,
  reviewId: string,
  reason?: string
): Promise<TeacherOwnReviewResponse> {
  const response = await apiFetch(`/api/v1/reviews/${reviewId}/flag`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ reason: reason ?? '' }),
  });
  if (response.status === 401) handleUnauthorized();
  if (!response.ok) throw new Error(`Failed to flag review: ${response.status}`);
  return response.json();
}
